import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Trash2 } from 'lucide-react';

interface Brochure {
  id: string;
  title: string;
  description: string | null;
  pdf_url: string;
  cover_image_url: string | null;
  created_at: string;
}

const AdminBrochures = () => {
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [brochures, setBrochures] = useState<Brochure[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBrochures = async () => {
    const { data, error } = await supabase
      .from('brochures')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setBrochures(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchBrochures();
  }, []);

  const uploadFile = async (file: File, prefix: string) => {
    const ext = file.name.split('.').pop();
    const path = `${prefix}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('brochures').upload(path, file);
    if (error) throw error;
    const { data } = supabase.storage.from('brochures').getPublicUrl(path);
    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !pdfFile) {
      toast({ title: 'Missing fields', description: 'Title and PDF are required.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const pdfUrl = await uploadFile(pdfFile, 'pdfs');
      let coverUrl: string | null = null;
      if (coverFile) coverUrl = await uploadFile(coverFile, 'covers');

      const { error } = await supabase.from('brochures').insert({
        title: title.trim(),
        description: description.trim() || null,
        pdf_url: pdfUrl,
        cover_image_url: coverUrl,
      });
      if (error) throw error;

      toast({ title: 'Brochure uploaded', description: 'Now visible on the Brochures page.' });
      setTitle('');
      setDescription('');
      setPdfFile(null);
      setCoverFile(null);
      (document.getElementById('pdf-input') as HTMLInputElement).value = '';
      const coverInput = document.getElementById('cover-input') as HTMLInputElement;
      if (coverInput) coverInput.value = '';
      fetchBrochures();
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this brochure?')) return;
    const { error } = await supabase.from('brochures').delete().eq('id', id);
    if (error) {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Deleted' });
      fetchBrochures();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="py-12 bg-primary text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Brochures Admin</h1>
          <p className="text-blue-100">Upload and manage brochures shown on the public page.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-12 space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Upload New Brochure</h2>

            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} required />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} rows={3} />
            </div>

            <div>
              <Label htmlFor="pdf-input">PDF File *</Label>
              <Input id="pdf-input" type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} required />
            </div>

            <div>
              <Label htmlFor="cover-input">Cover Image (optional)</Label>
              <Input id="cover-input" type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files?.[0] || null)} />
            </div>

            <Button type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Brochure'}
            </Button>
          </form>

          <h2 className="text-xl font-semibold text-foreground mb-4">Existing Brochures</h2>
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : brochures.length === 0 ? (
            <p className="text-muted-foreground">No brochures uploaded yet.</p>
          ) : (
            <div className="space-y-3">
              {brochures.map((b) => (
                <div key={b.id} className="flex items-center gap-4 border border-gray-200 rounded-lg p-4">
                  {b.cover_image_url ? (
                    <img src={b.cover_image_url} alt={b.title} className="w-16 h-20 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-20 bg-gray-100 rounded flex items-center justify-center text-xs text-muted-foreground">PDF</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground truncate">{b.title}</h3>
                    {b.description && <p className="text-sm text-muted-foreground truncate">{b.description}</p>}
                    <a href={b.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View PDF</a>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminBrochures;
